/*
 * ApplyHub — one memorable entry point ("/apply") for the two existing
 * public applications. No new form, no new backend — just routes a visitor
 * to the right EXISTING page (DriverApply.jsx or PartnerApply.jsx). All old
 * links (/drive/new, /drivers/apply, /partners/apply) still work unchanged.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Car, Buildings, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import PageBackLink from "../components/PageBackLink";

const CHOICES = [
  {
    key: "driver",
    Icon: Car,
    title: "I'm a driver",
    subtitle: "Independent driver",
    desc: "You drive your own vehicle and want to get dispatched trips directly.",
    bullets: ["Name, phone, email and home address to start", "Everything else can be added later", "~5 min"],
    to: "/drivers/apply",
    testid: "apply-hub-driver",
  },
  {
    key: "provider",
    Icon: Buildings,
    title: "I run a transportation company",
    subtitle: "Fleet / Transportation Provider",
    desc: "You own a fleet, employ drivers, and want trips dispatched to your company.",
    bullets: ["Contact info, address, service area and fleet size", "Insurance, COI, W-9 and banking can come later", "~5–10 min"],
    to: "/partners/apply",
    testid: "apply-hub-provider",
  },
];

export default function ApplyHub() {
  return (
    <main className="min-h-screen bg-bone px-4 py-10" data-testid="apply-hub-page">
      <PageBackLink fallbackTo="/" label="Back to home" style={{ padding: "0 0 16px" }} />
      <div className="max-w-4xl mx-auto text-center mb-10">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brandblue">MedexOVA</div>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black text-ink">Which one are you?</h1>
        <p className="mt-3 text-sm sm:text-base text-slate-500 max-w-xl mx-auto">
          Pick the option that fits — we'll take you straight to the right application.
        </p>
      </div>

      <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-6">
        {CHOICES.map(({ key, Icon, title, subtitle, desc, bullets, to, testid }) => (
          <Link
            key={key}
            to={to}
            data-testid={testid}
            className="group rounded-2xl border border-[#E2E0D8] bg-white p-7 text-left hover:border-brandblue hover:shadow-lg transition-all"
          >
            <div className="w-12 h-12 rounded-xl bg-forest/10 grid place-items-center">
              <Icon size={24} weight="fill" className="text-forest" />
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">{subtitle}</div>
            <h2 className="mt-1 font-heading text-xl font-black text-ink">{title}</h2>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{desc}</p>
            <ul className="mt-4 space-y-1.5">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-xs text-slate-500">
                  <CheckCircle size={14} className="mt-0.5 text-forest shrink-0" weight="fill" />
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brandblue group-hover:gap-2.5 transition-all">
              Start application <ArrowRight size={14} weight="bold" />
            </div>
          </Link>
        ))}
      </div>

      <div className="max-w-4xl mx-auto mt-8 text-center text-xs text-slate-500">
        Need trips for your facility, or want to refer business instead?{" "}
        <Link to="/partners" className="text-brandblue font-semibold underline" data-testid="apply-hub-facility-link">
          Go to the partner inquiry form →
        </Link>
      </div>
    </main>
  );
}
